import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Grid,
  MenuItem,
  Select,
  Stack,
  Tab,
  TextField,
  Tabs,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import CookieJs from "js-cookie";
import get from "lodash/get";
import dayjs, { Dayjs } from "dayjs";
import { useMemo, useState } from "react";
import { useMutation, useQuery } from "react-query";
import { Link as RouterLink } from "react-router-dom";
import Breadcrumb from "../../components/Breadcrumb";
import MainLayout from "../../components/MainLayout";
import ProtectedPermissionPage from "../../components/ProtectedPermissionPage";
import useNotification from "../../hooks/useNotification";
import groupService from "../../services/group";
import plService from "../../services/pl";
import queryDefault from "../../utils/queryDefault";

type TabValue = "list" | "summary_branch" | "summary_region";

const formatNumber = (value: any) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  const parsed = Number(value);
  if (Number.isNaN(parsed)) {
    return value;
  }

  return parsed.toLocaleString("id-ID", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

const toDateParam = (value: Dayjs | null, fallback: string) => {
  if (!value || !value.isValid()) {
    return fallback;
  }

  return value.format("YYYY-MM-DD");
};

const CustomNoRowOverlay = () => (
  <Box sx={{ pt: 5 }}>
    <Typography variant="body1" textAlign="center" fontWeight="600">
      Sorry, data is not found
    </Typography>
  </Box>
);

export default function ListPL() {
  const notification = useNotification();
  const user = JSON.parse(CookieJs.get("USER") || "{}");

  const today = dayjs();
  const startOfMonth = dayjs().startOf("month");

  const [tab, setTab] = useState<TabValue>("list");
  const [filters, setFilters] = useState({
    branch_group_id: 0,
    coa_code: "",
    date_start: startOfMonth.format("YYYY-MM-DD"),
    date_end: today.format("YYYY-MM-DD"),
  });

  const [uploading, setUploading] = useState(false);

  const listParams = useMemo(() => {
    return {
      branch_group_id: filters.branch_group_id || undefined,
      coa_code: filters.coa_code || undefined,
      date_start: filters.date_start,
      date_end: filters.date_end,
    };
  }, [filters]);

  const summaryParams = useMemo(
    () => ({
      date_start: filters.date_start,
      date_end: filters.date_end,
    }),
    [filters.date_start, filters.date_end]
  );

  const branchGroups = useQuery(
    ["branch-group-options"],
    () => groupService.getAllBranchGroup({ keyword: "" }),
    {
      ...queryDefault,
    }
  );

  const coaList = useQuery(
    ["pl-coa-options"],
    () => plService.getCOA(),
    {
      ...queryDefault,
    }
  );

  const listPL = useQuery(
    ["list-pl", listParams],
    ({ queryKey }) => plService.listPL(queryKey[1]),
    {
      ...queryDefault,
    }
  );

  const summaryBranch = useQuery(
    ["summary-pl-branch", summaryParams],
    ({ queryKey }) => plService.getSummaryBranch(queryKey[1]),
    {
      ...queryDefault,
    }
  );

  const summaryRegion = useQuery(
    ["summary-pl-region", summaryParams],
    ({ queryKey }) => plService.getSummaryRegion(queryKey[1]),
    {
      ...queryDefault,
    }
  );

  const summaryBranchData = get(summaryBranch, "data.data", []);
  const summaryBranchTotals = summaryBranchData.reduce(
    (acc: { totalIncome: number; totalExpense: number; netProfit: number }, item: any) => {
      return {
        totalIncome: acc.totalIncome + Number(get(item, "total_income", 0)),
        totalExpense: acc.totalExpense + Number(get(item, "total_expense", 0)),
        netProfit: acc.netProfit + Number(get(item, "net_profit", 0)),
      };
    },
    { totalIncome: 0, totalExpense: 0, netProfit: 0 }
  );

  const summaryRegionData = get(summaryRegion, "data.data", []);
  const summaryRegionTotalNetProfit = summaryRegionData.reduce(
    (acc: number, item: any) => acc + Number(get(item, "net_profit", 0)),
    0
  );

  const downloadTemplate = useMutation("download-pl-template", plService.downloadTemplate, {
    onError: () => {
      notification.onOpen({
        message: "Something went wrong when downloading template",
        type: "error",
        position: "top",
      });
    },
  });

  const uploadBulk = useMutation("upload-pl-bulk", plService.uploadBulkTemplate, {
    onSuccess: (response) => {
      setUploading(false);
      notification.onOpen({
        message: response?.message || "File was successfully uploaded!",
        type: "success",
        position: "top",
      });
      listPL.refetch();
      summaryBranch.refetch();
      summaryRegion.refetch();
    },
    onError: () => {
      setUploading(false);
      notification.onOpen({
        message: "Something wrong happened when uploading bulk template",
        type: "error",
        position: "top",
      });
    },
    ...queryDefault,
  });

  const handleUploadFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    const extension = file.name.split(".").pop()?.toLowerCase();
    if (extension !== "xls" && extension !== "xlsx") {
      notification.onOpen({
        message: "Please upload excel file only (.xls or .xlsx)",
        type: "error",
        position: "top",
      });
      event.target.value = "";
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("admin_name", user?.username || user?.name || "");

    setUploading(true);
    uploadBulk.mutate(formData);
    event.target.value = "";
  };

  const listColumns = [
    {
      field: "branch_group_name",
      headerName: "Branch Group",
      minWidth: 180,
      flex: 1,
      filterable: false,
    },
    {
      field: "kode_cabang",
      headerName: "Kode Cabang",
      minWidth: 140,
      flex: 1,
      filterable: false,
    },
    {
      field: "transaction_date",
      headerName: "Transaction Date",
      minWidth: 140,
      flex: 1,
      filterable: false,
    },
    {
      field: "coa_code",
      headerName: "COA Code",
      minWidth: 120,
      flex: 1,
      filterable: false,
    },
    {
      field: "coa_description",
      headerName: "COA Description",
      minWidth: 220,
      flex: 1,
      filterable: false,
    },
    {
      field: "coa_type",
      headerName: "COA Type",
      minWidth: 180,
      flex: 1,
      filterable: false,
    },
    {
      field: "amount",
      headerName: "Amount",
      minWidth: 140,
      flex: 1,
      filterable: false,
      renderCell: (params: any) => <Typography>{formatNumber(params.value)}</Typography>,
    },
    {
      field: "description",
      headerName: "Description",
      minWidth: 220,
      flex: 1,
      filterable: false,
    },
    {
      field: "accurate_no",
      headerName: "Accurate No",
      minWidth: 180,
      flex: 1,
      filterable: false,
    },
    {
      field: "source",
      headerName: "Source",
      minWidth: 100,
      flex: 1,
      filterable: false,
    },
    {
      field: "actions",
      headerName: "Action",
      minWidth: 130,
      sortable: false,
      filterable: false,
      renderCell: (params: any) => (
        <Button
          variant="outlined"
          color="primary"
          component={RouterLink}
          to={`/app/pl/edit/${get(params, "row.id")}`}
        >
          Edit
        </Button>
      ),
    },
  ];

  return (
    <MainLayout>
      <Breadcrumb label="P&L" breadcrumbs={[{ label: "P&L", href: "/app/pl" }]} />

      <ProtectedPermissionPage acceptPermissions={[1, 11]}>
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <FormLabel>Date Start</FormLabel>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    inputFormat="DD/MM/YYYY"
                    value={dayjs(filters.date_start)}
                    onChange={(newValue) => {
                      setFilters((prev) => ({
                        ...prev,
                        date_start: toDateParam(newValue, prev.date_start),
                      }));
                    }}
                    renderInput={(params) => <TextField {...params} size="small" />}
                  />
                </LocalizationProvider>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <FormLabel>Date End</FormLabel>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    inputFormat="DD/MM/YYYY"
                    value={dayjs(filters.date_end)}
                    onChange={(newValue) => {
                      setFilters((prev) => ({
                        ...prev,
                        date_end: toDateParam(newValue, prev.date_end),
                      }));
                    }}
                    renderInput={(params) => <TextField {...params} size="small" />}
                  />
                </LocalizationProvider>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <FormLabel>Branch Group</FormLabel>
                <Select
                  value={filters.branch_group_id}
                  onChange={(event) =>
                    setFilters((prev) => ({ ...prev, branch_group_id: Number(event.target.value) }))
                  }
                >
                  <MenuItem value={0}>All Branch Group</MenuItem>
                  {get(branchGroups, "data.data", []).map((item: any) => (
                    <MenuItem key={item.id} value={item.id}>
                      {item.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <FormLabel>COA</FormLabel>
                <Select
                  value={filters.coa_code}
                  onChange={(event) =>
                    setFilters((prev) => ({ ...prev, coa_code: String(event.target.value) }))
                  }
                >
                  <MenuItem value="">All COA</MenuItem>
                  {get(coaList, "data.data", []).map((item: any) => (
                    <MenuItem key={item.code} value={item.code}>
                      {item.code} - {item.description}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ mt: 2 }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <LoadingButton
              variant="contained"
              onClick={() => downloadTemplate.mutate()}
              loading={downloadTemplate.isLoading}
            >
              Download bulk template
            </LoadingButton>

            <LoadingButton variant="contained" component="label" loading={uploading}>
              Upload bulk template
              <input
                onChange={handleUploadFile}
                type="file"
                hidden
                accept=".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              />
            </LoadingButton>

            <Button variant="contained" component={RouterLink} to="/app/pl/create">
              Add New Record
            </Button>
          </Stack>
        </Box>

        <Box sx={{ mt: 3 }}>
          <Tabs value={tab} onChange={(_, value: TabValue) => setTab(value)}>
            <Tab value="list" label="List P&L" />
            <Tab value="summary_branch" label="Summary Branch" />
            <Tab value="summary_region" label="Summary Region" />
          </Tabs>
        </Box>

        {tab === "list" && (
          <Box sx={{ mt: 2 }}>
            <DataGrid
              autoHeight
              loading={listPL.isLoading}
              getRowId={(record) => get(record, "id")}
              rows={get(listPL, "data.data", [])}
              rowCount={get(listPL, "data.data", []).length}
              rowsPerPageOptions={[10, 20, 50]}
              pageSize={10}
              pagination
              disableSelectionOnClick
              components={{
                NoRowsOverlay: CustomNoRowOverlay,
              }}
              columns={listColumns as any}
              sx={{
                ".MuiDataGrid-columnHeaderTitle": {
                  fontWeight: 700,
                },
              }}
            />
          </Box>
        )}

        {tab === "summary_branch" && (
          <Box sx={{ mt: 2 }}>
            <DataGrid
              autoHeight
              loading={summaryBranch.isLoading}
              getRowId={(record) => `${get(record, "branch_group_id")}-${get(record, "kode_cabang")}`}
              rows={summaryBranchData}
              rowCount={summaryBranchData.length}
              rowsPerPageOptions={[10, 20, 50]}
              pageSize={10}
              pagination
              disableSelectionOnClick
              components={{
                NoRowsOverlay: CustomNoRowOverlay,
              }}
              columns={[
                {
                  field: "branch_group_name",
                  headerName: "Branch Group",
                  minWidth: 180,
                  flex: 1,
                  filterable: false,
                },
                {
                  field: "kode_cabang",
                  headerName: "Kode Cabang",
                  minWidth: 140,
                  flex: 1,
                  filterable: false,
                },
                {
                  field: "region",
                  headerName: "Region",
                  minWidth: 120,
                  flex: 1,
                  filterable: false,
                },
                {
                  field: "total_income",
                  headerName: "Total Income",
                  minWidth: 180,
                  flex: 1,
                  filterable: false,
                  renderCell: (params: any) => <Typography>{formatNumber(params.value)}</Typography>,
                },
                {
                  field: "total_expense",
                  headerName: "Total Expense",
                  minWidth: 180,
                  flex: 1,
                  filterable: false,
                  renderCell: (params: any) => <Typography>{formatNumber(params.value)}</Typography>,
                },
                {
                  field: "net_profit",
                  headerName: "Net Profit",
                  minWidth: 240,
                  flex: 1,
                  filterable: false,
                  renderCell: (params: any) => <Typography>{formatNumber(params.value)}</Typography>,
                },
              ]}
              sx={{
                ".MuiDataGrid-columnHeaderTitle": {
                  fontWeight: 700,
                },
              }}
            />

            <Box sx={{ mt: 2, p: 2, border: "1px solid #e0e0e0", borderRadius: 1 }}>
              <Typography fontWeight={700}>TOTAL</Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={3} sx={{ mt: 1 }}>
                <Typography>
                  Total Income: <strong>{formatNumber(summaryBranchTotals.totalIncome)}</strong>
                </Typography>
                <Typography>
                  Total Expense: <strong>{formatNumber(summaryBranchTotals.totalExpense)}</strong>
                </Typography>
                <Typography>
                  Total Net Profit: <strong>{formatNumber(summaryBranchTotals.netProfit)}</strong>
                </Typography>
              </Stack>
            </Box>
          </Box>
        )}

        {tab === "summary_region" && (
          <Box sx={{ mt: 2 }}>
            <DataGrid
              autoHeight
              loading={summaryRegion.isLoading}
              getRowId={(record) => `${get(record, "region")}-${get(record, "net_profit")}`}
              rows={summaryRegionData}
              rowCount={summaryRegionData.length}
              rowsPerPageOptions={[10, 20, 50]}
              pageSize={10}
              pagination
              disableSelectionOnClick
              components={{
                NoRowsOverlay: CustomNoRowOverlay,
              }}
              columns={[
                {
                  field: "region",
                  headerName: "Region",
                  minWidth: 220,
                  flex: 1,
                  filterable: false,
                },
                {
                  field: "net_profit",
                  headerName: "Net Profit",
                  minWidth: 180,
                  flex: 1,
                  filterable: false,
                  renderCell: (params: any) => <Typography>{formatNumber(params.value)}</Typography>,
                },
                {
                  field: "profit_ratio",
                  headerName: "Profit Ratio (%)",
                  minWidth: 180,
                  flex: 1,
                  filterable: false,
                  renderCell: (params: any) => <Typography>{formatNumber(params.value)}</Typography>,
                },
                {
                  field: "profit_contribution",
                  headerName: "Profit Contribution (%)",
                  minWidth: 180,
                  flex: 1,
                  filterable: false,
                  renderCell: (params: any) => <Typography>{formatNumber(params.value)}</Typography>,
                },
              ]}
              sx={{
                ".MuiDataGrid-columnHeaderTitle": {
                  fontWeight: 700,
                },
              }}
            />

            <Box sx={{ mt: 2, p: 2, border: "1px solid #e0e0e0", borderRadius: 1 }}>
              <Typography fontWeight={700}>TOTAL</Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={3} sx={{ mt: 1 }}>
                <Typography>
                  Total Net Profit: <strong>{formatNumber(summaryRegionTotalNetProfit)}</strong>
                </Typography>
              </Stack>
            </Box>
          </Box>
        )}
      </ProtectedPermissionPage>
    </MainLayout>
  );
}
